import React, { Suspense, lazy } from 'react';
import { Container, Typography, Box, IconButton } from '@mui/material';
import {
  GitHub as GitHubIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';

const Robot3D = lazy(() =>
  import('./Robot3D').then(module => ({ default: module.Robot3D }))
);

export const Home2 = (): React.ReactNode => {
  const socialLinks = [
    {
      icon: <GitHubIcon />,
      href: 'https://github.com/islommashanlo',
      label: 'GitHub',
    },
    {
      icon: <YouTubeIcon />,
      href: 'https://www.youtube.com/channel/UC0208gJUjWyIH0cJ6H9XoKA',
      label: 'YouTube',
    },
    {
      icon: <LinkedInIcon />,
      href: 'https://www.linkedin.com/in/islom-mashanlo/',
      label: 'LinkedIn',
    },
    {
      icon: <InstagramIcon />,
      href: 'https://www.instagram.com/isa.solaris/',
      label: 'Instagram',
    },
  ];

  return (
    <Container
      maxWidth={false}
      sx={{
        paddingBottom: '90px',
        paddingTop: '90px',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'linear-gradient(145deg, rgba(5, 12, 23, 0.95), rgba(10, 20, 36, 0.9))',
          borderTop: '1px solid rgba(34, 211, 238, 0.15)',
          borderBottom: '1px solid rgba(34, 211, 238, 0.08)',
          zIndex: -1,
        },
      }}
    >
      <Container maxWidth='lg'>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Box sx={{ flex: { md: '0 0 66%' } }}>
            <Typography
              variant='h2'
              sx={{
                fontSize: { xs: '1.8rem', md: '2.6rem' },
                fontWeight: 'bold',
                mb: 3,
                letterSpacing: '0.04em',
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Let me{' '}
              <Box
                component='span'
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  textShadow: '0 0 16px rgba(74, 222, 128, 0.35)',
                }}
              >
                introduce
              </Box>{' '}
              myself
            </Typography>

            <Typography
              variant='body1'
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.8,
                textAlign: 'justify',
                mb: 3.5,
              }}
            >
              I fell in love with programming when I was 16. I made different
              &quot;Mods&quot; for video games I used to play and enjoyed the
              surprising creativity this highly technical field provided.
              <br />
              <br />I am fluent in languages such as{' '}
              <Box
                component='span'
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  textShadow: '0 0 10px rgba(34, 211, 238, 0.35)',
                  fontStyle: 'italic',
                }}
              >
                Ruby, Javascript, Typescript, Go and Python.
              </Box>
              <br />
              <br />I love building new{' '}
              <Box
                component='span'
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  fontStyle: 'italic',
                }}
              >
                Web Applications, Microservices and Mobile Applications{' '}
              </Box>
              and solve different{' '}
              <Box
                component='span'
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                }}
              >
                Coding and Algorithmic challenges.
              </Box>
              <br />
              <br />
              Whenever I can, I also develop full-stack applications with{' '}
              <Box
                component='span'
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                }}
              >
                Ruby on Rails{' '}
              </Box>
              and{' '}
              <Box
                component='span'
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                  fontStyle: 'italic',
                }}
              >
                Modern Typescript Library and Frameworks{' '}
              </Box>
              like{' '}
              <Box
                component='span'
                sx={{
                  color: 'primary.main',
                  fontWeight: 'bold',
                }}
              >
                React.js and Next.js
              </Box>
            </Typography>
          </Box>

          <Box sx={{ flex: { md: '0 0 34%' } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: { xs: 4, md: 12 },
                pb: { xs: 4, md: 0 },
              }}
            >
              <Suspense
                fallback={
                  <Box
                    sx={{
                      width: '100%',
                      height: 360,
                      background:
                        'radial-gradient(circle at 30% 30%, rgba(199,112,240,0.2), transparent 60%)',
                      borderRadius: '50%',
                    }}
                  />
                }
              >
                <Robot3D />
              </Suspense>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography
            variant='h3'
            sx={{
              mb: 2,
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 'bold',
            }}
          >
            FIND ME ON
          </Typography>
          <Typography
            variant='body1'
            sx={{
              mb: 4,
              fontSize: '1.1rem',
            }}
          >
            Feel free to{' '}
            <Box
              component='span'
              sx={{
                color: 'primary.main',
                fontWeight: 'bold',
              }}
            >
              connect{' '}
            </Box>
            with me
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {socialLinks.map((social, index) => (
              <IconButton
                key={social.label}
                component='a'
                href={social.href}
                target='_blank'
                rel='noreferrer'
                aria-label={social.label}
                sx={{
                  color: 'primary.main',
                  backgroundColor: 'rgba(34, 211, 238, 0.08)',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  transition: 'all 0.3s ease',
                  boxShadow:
                    '0 0 0 1px rgba(34, 211, 238, 0.25), 0 14px 40px -20px rgba(34, 211, 238, 0.4)',
                  '&:hover': {
                    backgroundColor: 'rgba(34, 211, 238, 0.2)',
                    color: 'white',
                    transform: 'translateY(-5px)',
                    borderColor: 'primary.main',
                    boxShadow:
                      '0 0 0 1px rgba(74, 222, 128, 0.45), 0 18px 48px -18px rgba(74, 222, 128, 0.55)',
                  },
                  '&:nth-of-type(1)': { animationDelay: `${index * 0.1}s` },
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Container>
    </Container>
  );
};
